
// import React from 'react';
import React, { useCallback, useEffect, useState } from 'react';

import { FloatingFeature, ModelBase, PlayerObject, SmartHash, UpsertableBase } from "@models";
import { Race, Background, Condition, Creature, Feat, Gift, Subrace, Item, MagicItemKeyword, Proficiency, SpecialFeature, StandardFeatures, Transform, SpellcastingFeature } from '@dndModels';

import MyTTRPGAPI from '@dndUtilities/system_api';

import NonFSIndex from '../../components/NonFSIndex';
import LoadingBox from "@components/input/LoadingBox";
import CheckBox from '@components/input/CheckBox';
import ButtonBox from '@components/input/buttons/ButtonBox';

const api = MyTTRPGAPI.getInstance();

export default function FloatingFeatureIndex() {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [loading_message, setLoadingMessage] = useState("");
  const [creatures, setCreatures] = useState<Creature[]>([]);
  const [floating_features, setFloatingFeatures] = useState<FloatingFeature[]>([]);
  const [ff_name_mapping, setFFNameMapping] = useState<{ [name: string]: string[] }>({});
  const [duplicates_only, setDuplicatesOnly] = useState(false);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);

  const load = useCallback(() => {
    setLoading(true);
    const onComplete = (res: SmartHash) => {
      setCreatures(res.creature as Creature[]);
      const new_ffs = res.floating_feature as FloatingFeature[];
      setFloatingFeatures(new_ffs);
      const new_mapping: { [name: string]: string[] } = {};
      const names: string[] = [];
      for (const ff of new_ffs) {
        if (!names.includes(ff.name)) {
          names.push(ff.name);
          new_mapping[ff.name] = [ff._id];
        } else {
          new_mapping[ff.name].push(ff._id);
        }
      }
      setFFNameMapping(new_mapping);
      setLoading(false);
    }
    const onProgress = (data_type: string, percentage: number) => {
      if (percentage !== 0) {
        setLoadingMessage(`Loading Creatures ${Math.floor(percentage * 100)}%`);
      } else {
        setLoadingMessage(`Loading Creatures`);
      }
    }
    api.getSetOfObjectsWithCallbacks({ data_types: ["creature","floating_feature","spell_list"], onComplete, onProgress });
  }, []);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, load]);

  const inFS = (obj: UpsertableBase) => {
    // Identify a thing that has it
    const standard = (api.smart_hash.standard_features as StandardFeatures[]).find(o => o.feature_bases.find(o2 => o2.features.find(o3 => o3.floating_feature_id === obj._id)));
    if (standard) {
      return `In Standard Features: ${standard.true_name}`;
    }
    const ancestry = (api.smart_hash.ancestry as Race[]).find(o => o.feature_bases.find(o2 => o2.features.find(o3 => o3.floating_feature_id === obj._id)));
    if (ancestry) {
      return `In Race: ${ancestry.true_name}`;
    }
    const heritage = (api.smart_hash.heritage as Subrace[]).find(o => o.feature_bases.find(o2 => o2.features.find(o3 => o3.floating_feature_id === obj._id)));
    if (heritage) {
      return `In Subrace: ${heritage.true_name}`;
    }
    const background = (api.smart_hash.background as Background[]).find(o => o.feature_bases.find(o2 => o2.features.find(o3 => o3.floating_feature_id === obj._id)));
    if (background) {
      return `In Background: ${background.true_name}`;
    }
    const special_feature = (api.smart_hash.special_feature as SpecialFeature[]).find(o => o.feature_bases.find(o2 => o2.features.find(o3 => o3.floating_feature_id === obj._id)));
    if (special_feature) {
      return `In Special Feature: ${special_feature.true_name}`;
    }
    const feat = (api.smart_hash.feat as Feat[]).find(o => o.feature_bases.find(o2 => o2.features.find(o3 => o3.floating_feature_id === obj._id)));
    if (feat) {
      return `In Feat: ${feat.true_name}`;
    }
    const gift = (api.smart_hash.gift as Gift[]).find(o => o.feature_bases.find(o2 => o2.features.find(o3 => o3.floating_feature_id === obj._id)));
    if (gift) {
      return `In Gift: ${gift.true_name}`;
    }
    const item = (api.smart_hash.item as Item[]).find(o => o.feature_bases.find(o2 => o2.features.find(o3 => o3.floating_feature_id === obj._id)));
    if (item) {
      return `In Item: ${item.true_name}`;
    }
    const magic_item_keyword = (api.smart_hash.magic_item_keyword as MagicItemKeyword[]).find(o => o.feature_bases.find(o2 => o2.features.find(o3 => o3.floating_feature_id === obj._id)));
    if (magic_item_keyword) {
      return `In Magic Item Keyword: ${magic_item_keyword.true_name}`;
    }
    const condition = (api.smart_hash.condition as Condition[]).find(o => o.feature_bases.find(o2 => o2.features.find(o3 => o3.floating_feature_id === obj._id)));
    if (condition) {
      return `In Condition: ${condition.true_name}`;
    }
    const transform = (api.smart_hash.transform as Transform[]).find(o => o.features.find(o3 => o3.floating_feature_id === obj._id));
    if (transform) {
      return `In Transform: ${transform.true_name}`;
    }
    // const summon = (api.smart_hash.summon as Summon[]).find(o => o.features.find(o3 => o3.floating_feature_id === obj._id));
    // if (summon) {
    //   return `In Summon: ${summon.true_name}`;
    // }
    const creature = creatures.find(o => o.fbs.find(o2 => o2.features.find(o3 => o3.floating_feature_id === obj._id)));
    if (creature) {
      return `In Creature: ${creature.true_name}`;
    }
    return "";
  }

  const quickMerge = async (obj: UpsertableBase) => {
    if (obj instanceof FloatingFeature) {
      console.log(`Quick Merging: ${obj.name}`);
      const ids_from_map = ff_name_mapping[obj.name];
      if (ids_from_map && ids_from_map.length > 1) {
        const new_obj = obj;
        const duplicates = floating_features.filter(o => ids_from_map.includes(o._id));
        for (const merge_floating_feature of duplicates) {
          if (merge_floating_feature._id !== new_obj._id) {
            new_obj.merged_ids = [
              ...new_obj.merged_ids,
              merge_floating_feature._id,
              ...merge_floating_feature.merged_ids
            ];
          }
        }
        setProcessing(true);
        await api.upsertObject(new_obj.data_type, new_obj);
        for (const merge_floating_feature of duplicates) {
          if (merge_floating_feature._id !== new_obj._id) {
            await api.deleteObject(new_obj.data_type, merge_floating_feature);
          }
        }
        setProcessing(false);
        setReloading(true);
      }
    }
  }

  if (!fetched || loading || reloading) {
    return (<LoadingBox message={loading_message} size={window.innerHeight * 0.5} height={window.innerHeight - 81} />);
  } else {
    return (
      <NonFSIndex
        data_type="floating_feature"
        quick_fields={["feature_type"]}
        extra_by_add={() => {
          return (
            <>
              <CheckBox name="Duplicates Only" value={duplicates_only} 
                onChange={(changed) => {
                  setDuplicatesOnly(changed);
                  setReloading(true);
                }} 
              />
            </>
          );
        }}
        extra_filter={(obj) => {
          if (duplicates_only) {
            const ids_from_map = ff_name_mapping[obj.name];
            if (ids_from_map && ids_from_map.length > 1) {
              return true;
            }
            return false;
          }
          // if (obj instanceof SpellcastingFeature) {
          //   const spell_lists = api.smart_hash.spell_list.filter(o => obj.spell_list_ids.includes(o._id));
          //   if (obj.spell_list_ids.length !== spell_lists.length) {
          //     return true;
          //   }
          // }
          return true;
          // return false;
        }}
        extra_details={(obj) => {
          // if (obj instanceof WeaponKeywordUpgradeFeature) {
          let details = inFS(obj);
          // }
          const ids_from_map = ff_name_mapping[obj.name];
          if (ids_from_map && ids_from_map.length > 1) {
            if (details.length > 0) {
              details += "; ";
            }
            details += `Duplicates?`;
          }
          return details;
        }}
        extra={(obj) => {
          const ids_from_map = ff_name_mapping[obj.name];
          if (ids_from_map && ids_from_map.length > 1) {
            return (
              <ButtonBox name="Quick Merge"
                disabled={processing}
                onClick={() => {
                  quickMerge(obj);
                }}
              />
            );
          }
          return <></>;
        }}
      />
    );
  }
}
